import { controlPanelShell as fdy } from "./foundry-parts.generated";
/**
 * @fileoverview SettingsSidebarLink — one nav item in the SettingsSidebar.
 * Composes an icon + name + optional shortcut hint slot (e.g. "ctrl + J").
 * Active state highlights the row.
 *
 * Foundry mapping: `control-panel-shell__sidebar-link` (selected modifier:
 * `__sidebar-link-selected`).
 */

import { Icon, type IconName, KeyComboTag } from "@blueprintjs/core";

interface SettingsSidebarLinkProps {
  icon: IconName;
  name: string;
  /** Active state — typically derived from current route. */
  active?: boolean;
  /** Optional keyboard shortcut shown on the right (e.g. "ctrl+j"). */
  keyCombo?: string;
  /** Anchor href — if provided, renders as <a>. */
  href?: string;
  onClick?: () => void;
  className?: string;
}

export function SettingsSidebarLink({
  icon,
  name,
  active = false,
  keyCombo,
  href,
  onClick,
  className,
}: SettingsSidebarLinkProps) {
  /*
    THE CLASSES ARE FOUNDRY'S. The element structure here was already right —
    `li > a > div > (icon, name, label)` matches the capture exactly — and every
    one of them carried only a `bp-*` name, so none of the vendored rules could
    reach it. The rail rendered as a bulleted list with the icon stacked above
    its label, which is a `<ul>` with no rules applied rather than a layout
    anyone chose.
  */
  /*
    THREE THINGS THE CAPTURE HAS PER LINK THAT WE DID NOT.

      1. `__new-feature-indicator__` wraps the item. Foundry hangs its
         "something is new here" dot on it; we have no such feature, and the
         element still belongs because it is the box the item sits in — dropping
         it put every icon, name and label one level shallower than its
         counterpart, which is 154 nodes of the sidebar's difference.
      2. `bp6-text-overflow-ellipsis` on the name. A long settings label
         truncates upstream and wrapped here.
      3. `__sidebar-item-label__` is ALWAYS rendered, empty when there is no
         shortcut. It is a layout slot, not a conditional: rendering it only
         sometimes made the row's grid differ between rows.
  */
  const inner = (
    <div className={`${fdy["sidebar-item"]}`}>
      <Icon
        icon={icon}
        size={14}
        className={`${fdy["sidebar-item-icon"]}`}
      />
      <div
        className={`${fdy["sidebar-item-name"]} bp6-text-overflow-ellipsis`}
      >
        {name}
      </div>
      <div className={`${fdy["sidebar-item-label"]}`}>
        {keyCombo ? <KeyComboTag combo={keyCombo} minimal /> : null}
      </div>
    </div>
  );

  return (
    <li data-testid="settings-sidebar-link"
      className={[
        fdy["sidebar-link"],
        active ? fdy["sidebar-link-selected"] : "",
        active ? "is-selected" : "",
        className,
      ].filter(Boolean).join(" ")}
    >
      {href ? (
        <a href={href} onClick={onClick} className={`${fdy["sidebar-link-anchor"]}`}>
          <div className={fdy["new-feature-indicator"]}>{inner}</div>
        </a>
      ) : (
        <button
          type="button"
          onClick={onClick}
          className={`${fdy["sidebar-link-anchor"]}`}
        >
          <div className={fdy["new-feature-indicator"]}>{inner}</div>
        </button>
      )}
    </li>
  );
}
